import type { ReactNode } from "react"
import { ButtonBase, Skeleton, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    chip: {
        height: "2.5rem",
        padding: "0 .875rem",
        borderRadius: "6.25rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.background.paper,
        whiteSpace: "nowrap",
        transition: theme.transitions.create(["background-color", "border-color"], {
            duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
            borderColor: theme.palette.tailwind.slate[300],
        },
    },
    chipSelected: {
        background: theme.palette.tailwind.blue[50],
        borderColor: theme.palette.tailwind.blue[50],
        "&:hover": {
            background: theme.palette.tailwind.blue[100],
            borderColor: theme.palette.tailwind.blue[100],
        },
    },
    label: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[600],
    },
    labelSelected: {
        color: theme.palette.primary.main,
    },
    count: {
        padding: "0 .5rem",
        borderRadius: "6.25rem",
        fontWeight: 700,
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[600],
    },
    countSkeleton: {
        width: "1.5rem",
        height: "1.25rem",
        borderRadius: "6.25rem",
    },
    countSelected: {
        background: theme.palette.tailwind.blue[100],
        color: theme.palette.primary.main,
    },
}))

type Props = Readonly<{
    isSelected: boolean
    count?: number
    isCountLoading?: boolean
    onClick: () => void
    children: ReactNode
}>

export function StandardCountFilterChip({ isSelected, count, isCountLoading, onClick, children }: Props) {
    const { classes, cx } = useStyles()

    return (
        <Stack
            component={ButtonBase}
            direction="row"
            alignItems="center"
            spacing=".5rem"
            aria-pressed={isSelected}
            className={cx(classes.chip, isSelected && classes.chipSelected)}
            onClick={onClick}
        >
            <Typography
                variant="body2"
                component="span"
                className={cx(classes.label, isSelected && classes.labelSelected)}
            >
                {children}
            </Typography>
            {isCountLoading && <Skeleton variant="rounded" className={classes.countSkeleton} />}
            {!isCountLoading && count !== undefined && (
                <Typography
                    variant="body2"
                    component="span"
                    className={cx(classes.count, isSelected && classes.countSelected)}
                >
                    {count}
                </Typography>
            )}
        </Stack>
    )
}
